import { useState, useMemo } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Slider } from '@/components/ui/slider';
import { Users, Clock, Stethoscope } from 'lucide-react';

const RECOVERY_PER_PROVIDER = 60000;
const SUBSCRIPTION_PER_PROVIDER_MONTHLY = 449;
const MINS_SAVED_PER_NOTE = 15;

export default function ROICalculator() {
  const [ref, isVisible] = useScrollAnimation(0.1);
  const [providers, setProviders] = useState(5);
  const [patientsPerDay, setPatientsPerDay] = useState(25);
  const [adminHours, setAdminHours] = useState(12);

  const results = useMemo(() => {
    const annualSubscription = providers * SUBSCRIPTION_PER_PROVIDER_MONTHLY * 12;
    const totalRecovery = providers * RECOVERY_PER_PROVIDER;
    const netImpact = totalRecovery - annualSubscription;
    const dailyTimeSaved = (patientsPerDay * MINS_SAVED_PER_NOTE) / 60;
    const weeklyTimeSaved = dailyTimeSaved * 5;
    const annualTimeSaved = weeklyTimeSaved * 50 * providers;
    return { annualSubscription, totalRecovery, netImpact, weeklyTimeSaved, annualTimeSaved };
  }, [providers, patientsPerDay, adminHours]);

  const formatCurrency = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  const scrollToDemo = () => {
    const el = document.querySelector('#demo-form');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="roi-calculator" data-testid="roi-calculator-section" className="py-24 md:py-32 px-4 md:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        <div ref={ref} className={`scroll-fade-up ${isVisible ? 'visible' : ''}`}>
          <p className="uppercase text-xs tracking-[0.25em] text-vf-forest font-body font-bold mb-4 text-center">
            ROI Calculator
          </p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-vf-forest tracking-[-0.01em] mb-4 text-center leading-tight">
            See Your Revenue Recovery Potential.
          </h2>
          <p className="font-body text-base md:text-lg font-medium text-vf-charcoal text-center max-w-2xl mx-auto mb-16">
            Adjust the sliders to match your clinic. See how much VitalsFlow AI can recover for you.
          </p>
        </div>

        <div className={`scroll-fade-up ${isVisible ? 'visible' : ''} grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12`}>
          {/* Sliders */}
          <div className="lg:col-span-3 space-y-10">
            {/* Provider slider */}
            <div data-testid="roi-slider-providers" className="bg-vf-salt border border-vf-sage p-6 md:p-8">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <Users size={20} strokeWidth={1.5} className="text-vf-forest" />
                  <span className="font-body text-sm font-bold text-vf-charcoal uppercase tracking-[0.1em]">Number of Providers</span>
                </div>
                <span className="font-heading font-bold text-3xl text-vf-forest">{providers}</span>
              </div>
              <Slider
                value={[providers]}
                onValueChange={(v) => setProviders(v[0])}
                min={1}
                max={20}
                step={1}
                className="[&_[role=slider]]:bg-vf-forest [&_[role=slider]]:border-vf-forest [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_.relative>span]:bg-vf-mint [&_.relative]:bg-vf-sage"
              />
              <div className="flex justify-between mt-2">
                <span className="font-body text-xs text-vf-charcoal/40">1</span>
                <span className="font-body text-xs text-vf-charcoal/40">20</span>
              </div>
            </div>

            {/* Patients slider */}
            <div data-testid="roi-slider-patients" className="bg-vf-salt border border-vf-sage p-6 md:p-8">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <Stethoscope size={20} strokeWidth={1.5} className="text-vf-forest" />
                  <span className="font-body text-sm font-bold text-vf-charcoal uppercase tracking-[0.1em]">Avg. Patients per Day</span>
                </div>
                <span className="font-heading font-bold text-3xl text-vf-forest">{patientsPerDay}</span>
              </div>
              <Slider
                value={[patientsPerDay]}
                onValueChange={(v) => setPatientsPerDay(v[0])}
                min={15}
                max={40}
                step={1}
                className="[&_[role=slider]]:bg-vf-forest [&_[role=slider]]:border-vf-forest [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_.relative>span]:bg-vf-mint [&_.relative]:bg-vf-sage"
              />
              <div className="flex justify-between mt-2">
                <span className="font-body text-xs text-vf-charcoal/40">15</span>
                <span className="font-body text-xs text-vf-charcoal/40">40</span>
              </div>
            </div>

            {/* Admin hours slider */}
            <div data-testid="roi-slider-admin-hours" className="bg-vf-salt border border-vf-sage p-6 md:p-8">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <Clock size={20} strokeWidth={1.5} className="text-vf-forest" />
                  <span className="font-body text-sm font-bold text-vf-charcoal uppercase tracking-[0.1em]">Admin Hours / Week</span>
                </div>
                <span className="font-heading font-bold text-3xl text-vf-forest">{adminHours}</span>
              </div>
              <Slider
                value={[adminHours]}
                onValueChange={(v) => setAdminHours(v[0])}
                min={5}
                max={20}
                step={1}
                className="[&_[role=slider]]:bg-vf-forest [&_[role=slider]]:border-vf-forest [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_.relative>span]:bg-vf-mint [&_.relative]:bg-vf-sage"
              />
              <div className="flex justify-between mt-2">
                <span className="font-body text-xs text-vf-charcoal/40">5 hrs</span>
                <span className="font-body text-xs text-vf-charcoal/40">20 hrs</span>
              </div>
            </div>
          </div>

          {/* Results panel */}
          <div className="lg:col-span-2">
            <div data-testid="roi-results-panel" className="bg-vf-forest p-8 md:p-10 h-full flex flex-col justify-between">
              <div>
                <p className="uppercase text-[10px] tracking-[0.25em] text-vf-mint/70 font-body font-bold mb-8">
                  Your Estimated Impact
                </p>

                {/* Big number */}
                <div className="mb-10">
                  <p className="font-body text-xs text-white/50 uppercase tracking-[0.15em] mb-2">
                    Estimated Annual Revenue Gain
                  </p>
                  <p data-testid="roi-result-amount" className="font-heading font-bold text-4xl sm:text-5xl text-vf-mint leading-none">
                    {formatCurrency(results.netImpact)}
                  </p>
                  <p className="font-body text-xs text-white/40 mt-2">
                    After {formatCurrency(results.annualSubscription)}/yr subscription
                  </p>
                </div>

                {/* Breakdown */}
                <div className="space-y-5 border-t border-white/10 pt-6">
                  <div>
                    <p className="font-body text-xs text-white/50 uppercase tracking-[0.12em] mb-1">Revenue Recovered</p>
                    <p className="font-heading font-bold text-2xl text-white">{formatCurrency(results.totalRecovery)}</p>
                    <p className="font-body text-xs text-white/30 mt-0.5">{formatCurrency(RECOVERY_PER_PROVIDER)} per provider &times; {providers}</p>
                  </div>
                  <div>
                    <p className="font-body text-xs text-white/50 uppercase tracking-[0.12em] mb-1">Time Saved Weekly</p>
                    <p className="font-heading font-bold text-2xl text-white">{Math.round(results.weeklyTimeSaved)} hrs</p>
                    <p className="font-body text-xs text-white/30 mt-0.5">{patientsPerDay} patients &times; {MINS_SAVED_PER_NOTE} min saved per note</p>
                  </div>
                  <div>
                    <p className="font-body text-xs text-white/50 uppercase tracking-[0.12em] mb-1">Admin Hours Reclaimed / Year</p>
                    <p className="font-heading font-bold text-2xl text-white">{Math.round(results.annualTimeSaved).toLocaleString()} hrs</p>
                  </div>
                </div>
              </div>

              <button
                data-testid="roi-cta-button"
                onClick={scrollToDemo}
                className="mt-8 w-full font-body text-sm font-bold uppercase tracking-[0.18em] bg-vf-mint text-vf-charcoal px-6 py-4 shadow-lg hover:shadow-xl hover:bg-vf-mint/85 transition-all duration-300"
              >
                Recover Your Revenue
              </button>
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <p className={`scroll-fade-up ${isVisible ? 'visible' : ''} text-center font-body text-sm text-vf-charcoal/50 mt-10`}>
          Your clinic is losing an estimated <span className="font-bold text-vf-forest">{formatCurrency(results.totalRecovery)}</span> per year.
          VitalsFlow AI recovers $60,000+ per provider.
        </p>
      </div>
    </section>
  );
}
